import { useMemo } from "react";
import type { InventoryBalance, Location } from "../../api/types";
import { ConsumableItemPanel } from "../consumables/ConsumableItemPanel";
import { Modal } from "../Modal";

/**
 * G-94 S4 — lifted out of `LocationDashboardPage` unchanged, so the inventory
 * page can offer it once that page retires. Same markup, same calls, same
 * rules; only the file it lives in is different.
 */
export function ConsumablesModal({
  location,
  rows,
  onClose,
  onInventoryChanged,
}: {
  location: Location;
  rows: InventoryBalance[];
  onClose: () => void;
  onInventoryChanged: () => void;
}) {
  const cmItemRows = useMemo(() => {
    const byItem = new Map<string, InventoryBalance["item"]>();
    for (const row of rows) {
      if (row.item.defaultUom !== "CM") continue;
      const q = parseFloat(row.qtyOnHand);
      if (!Number.isFinite(q) || q <= 0) continue;
      byItem.set(row.item.id, row.item);
    }
    return [...byItem.values()];
  }, [rows]);

  return (
    <Modal title="Consumables (paper / ribbon)" onClose={onClose}>
      <p style={{ color: "var(--color-text-muted)", marginTop: 0 }}>
        Use any in-stock CM roll — presets (25 / 50 / 85 cm) or custom length. Stock is deducted from
        the batch&apos;s zone (dry storage, front, etc.); no move step required.
      </p>
      {cmItemRows.length === 0 ? (
        <div className="empty-state">
          No CM consumables in stock. Receive paper or ribbon to any zone first.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
          {cmItemRows.map((item) => (
            <ConsumableItemPanel
              key={item.id}
              locationId={location.id}
              item={item}
              onChanged={() => onInventoryChanged()}
            />
          ))}
        </div>
      )}
      <div className="button-row" style={{ marginTop: "var(--space-4)" }}>
        <button type="button" className="button plain" onClick={onClose}>
          Close
        </button>
      </div>
    </Modal>
  );
}
